import { Trans, useLingui } from '@lingui/react/macro';
import type { DayOfWeek } from '@repo/contracts';
import { useFlag } from '@repo/flags/react';
import { CalendarClock, Layers } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { dayCapacity, type DemandForecast } from '@/domain/templates/demand-scaling';
import { buildShiftColumns, computeVariantByShiftId } from '@/domain/templates/preview-columns';
import { buildShiftStacks } from '@/domain/templates/preview-stacks';
import { summarisePreview } from '@/domain/templates/preview-stats';
import { formatHoursDisplay, type PreviewMode } from '@/domain/templates/preview-utils';
import {
  fixedDayCount,
  resolveScaledInstanceCount,
  type DayCountResolver,
} from '@/domain/templates/staffing';
import { cn } from '@/lib/utils';
import type { DemandScaling, ShiftSet } from '@/types/template';

import { DemandControl } from './demand-control';
import { PreviewGrid } from './preview-grid';
import { StackedGrid } from './stacked-grid';
import { StaffGroupPreview } from './staff-group-preview';

interface LivePreviewProps {
  /** Saved shift patterns to render. */
  shifts: ShiftSet[];
  /** The pattern currently being edited, rendered as draft (ghost) pills. */
  draftShift?: ShiftSet | null;
  /** Whether to show the Shifts/Hours/Days stats panel below the grid. */
  showStats?: boolean;
  /** Template demand-scaling settings; drives the DemandControl + scaled counts. */
  demandScaling?: DemandScaling;
}

/**
 * The Build workspace's live template preview: a **Shift type / Staff group**
 * grouping toggle plus a **stacking** toggle, over a Mon–Sun grid. Stacked (the
 * default) collapses shifts of the same type+time per day into a `×N` deck
 * ({@link StackedGrid}); un-stacked shows one pill per required head
 * ({@link PreviewGrid}). The staff-group view applies the same stacking per
 * group. When scaling is enabled a {@link DemandControl} sets a what-if forecast
 * and the per-day counts scale against the baseline. Updates as the left pane
 * changes — no backend call.
 */
export function LivePreview({
  shifts,
  draftShift = null,
  showStats = true,
  demandScaling,
}: LivePreviewProps) {
  const { t } = useLingui();
  const [isScalingEnabled] = useFlag('TEMPLATE_BUILDER_SCALING');
  const [previewMode, setPreviewMode] = useState<PreviewMode>('shift-type');
  const [stacked, setStacked] = useState(true);
  const [forecast, setForecast] = useState<DemandForecast>({});

  // Drop a stale what-if when the template's metric/period changes (e.g. the user
  // edits the baseline), so a leftover weekly/daily forecast can't resurface.
  useEffect(() => {
    setForecast({});
  }, [demandScaling?.metric, demandScaling?.period]);

  // Scaling drives the preview only when the flag is on and the template opted in.
  const scalingActive = isScalingEnabled && demandScaling?.enabled === true ? demandScaling : null;
  // When scaling is active, counts vary per day against the baseline and what-if
  // forecast; otherwise the fixed staffing count is used everywhere.
  const resolveCount: DayCountResolver = scalingActive
    ? (shift, dayOfWeek) =>
        resolveScaledInstanceCount(
          shift,
          dayCapacity(scalingActive, forecast, dayOfWeek as DayOfWeek),
        )
    : fixedDayCount;

  const variantByShiftId = computeVariantByShiftId(shifts);
  const columns = buildShiftColumns(shifts, draftShift, variantByShiftId, resolveCount);
  const stackColumns = buildShiftStacks(shifts, draftShift, resolveCount);
  const isEmpty = shifts.length === 0 && draftShift === null;
  // Stats reflect everything the grid draws — the saved shifts plus the in-progress
  // draft — so the totals stay in step with the preview (and update live as the
  // forecast changes). Without the draft they'd read 0 while creating the first
  // pattern, which looks broken.
  const statsShifts = draftShift ? [...shifts, draftShift] : shifts;
  const stats = summarisePreview(statsShifts, resolveCount);

  if (isEmpty) {
    return (
      <div
        data-testid="live-preview"
        className="flex h-full flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center text-muted-foreground"
      >
        <CalendarClock className="mb-2 size-7 opacity-40" />
        <p className="text-sm font-medium text-foreground">
          <Trans>No shifts yet</Trans>
        </p>
        <p className="text-xs">
          <Trans>Add a shift pattern to see it appear here.</Trans>
        </p>
      </div>
    );
  }

  return (
    <div data-testid="live-preview" className="flex flex-col gap-3">
      {scalingActive && (
        <DemandControl scaling={scalingActive} forecast={forecast} onForecastChange={setForecast} />
      )}

      <div className="flex items-center justify-between gap-2">
        <Tabs value={previewMode} onValueChange={(value) => setPreviewMode(value as PreviewMode)}>
          <TabsList>
            <TabsTrigger value="shift-type">
              <Trans>Shift type</Trans>
            </TabsTrigger>
            <TabsTrigger value="staff-group">
              <Trans>Staff group</Trans>
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <Button
          variant="ghost"
          size="icon"
          aria-pressed={stacked}
          aria-label={t`Stack shifts of the same type`}
          onClick={() => setStacked((value) => !value)}
          // A solid `secondary` (dark) background with a `text-primary` icon on
          // top is a ~1.2:1 contrast ratio — well under the 3:1 WCAG minimum for
          // UI components. `--button-toggle-active-background` is a light tint
          // that keeps the primary-coloured icon readable when toggled on.
          className={cn(stacked && 'bg-[var(--button-toggle-active-background)]')}
        >
          <Layers className={cn('size-4', stacked && 'text-primary')} />
        </Button>
      </div>

      {showStats && (
        <div
          data-testid="preview-stats"
          className="grid grid-cols-3 gap-px overflow-hidden rounded-lg border bg-border"
        >
          <PreviewStat value={String(stats.shifts)} label={<Trans>Shifts</Trans>} />
          <PreviewStat value={`${formatHoursDisplay(stats.hours)}h`} label={<Trans>Hours</Trans>} />
          <PreviewStat value={`${stats.days}/7`} label={<Trans>Days</Trans>} />
        </div>
      )}

      {previewMode === 'staff-group' ? (
        <StaffGroupPreview
          shifts={shifts}
          draftShift={draftShift}
          stacked={stacked}
          resolveCount={resolveCount}
        />
      ) : stacked ? (
        <StackedGrid columns={stackColumns} />
      ) : (
        <PreviewGrid columns={columns} />
      )}
    </div>
  );
}

/** A single value/label cell in the preview's rota-details panel. */
function PreviewStat({ value, label }: { value: string; label: ReactNode }) {
  return (
    <div className="bg-card p-3 text-center">
      <div className="text-[length:var(--section-title-font-size)] leading-[var(--section-title-line-height)] font-semibold">
        {value}
      </div>
      <div className="mt-0.5 text-[length:var(--extra-small-font-size)] leading-[var(--extra-small-line-height)] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
    </div>
  );
}
